/**
 * Admin Dashboard — Stats, live activity, channel status, daily AI summary.
 * Talk2 light theme.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Users, Radio, Hash, Mic, Wifi, Activity, BookOpen, ChevronRight, BarChart2 } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// ── Card primitives ────────────────────────────────────────────
function Card({ children, className = '', accent }) {
  return (
    <div
      className={`rounded-md ${className}`}
      style={{
        background: 'rgba(255,255,255,0.78)',
        WebkitBackdropFilter: 'saturate(180%) blur(30px)',
        backdropFilter: 'saturate(180%) blur(30px)',
        border: '1px solid rgba(0,0,0,0.04)',
        boxShadow: '0 8px 24px rgba(0,0,0,.06), 0 2px 6px rgba(0,0,0,.04)',
        ...(accent ? { borderTop: `3px solid ${accent}` } : {}),
      }}
    >
      {children}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, sub }) {
  return (
    <Card className="p-5" accent={color}>
      <div className="flex items-start justify-between mb-3">
        <div
          className="w-10 h-10 rounded-md flex items-center justify-center"
          style={{ background: `${color}1f`, color }}
        >
          <Icon size={18} />
        </div>
        <span className="text-3xl font-extrabold text-n-800 tracking-apple-tighter">{value ?? '—'}</span>
      </div>
      <div className="text-sm font-semibold text-n-700 tracking-apple-tight">{label}</div>
      {sub && <div className="text-xs text-n-500 mt-0.5">{sub}</div>}
    </Card>
  );
}

function ActivityBadge({ type }) {
  const map = {
    ptt:        { label: 'PTT',  color: '#007AFF', bg: '#E6F1FF' },
    join:       { label: 'JOIN', color: '#34C759', bg: '#E5F9EC' },
    connect:    { label: 'ON',   color: '#34C759', bg: '#E5F9EC' },
    disconnect: { label: 'OFF',  color: '#FF3B30', bg: '#FFE5EC' },
  };
  const s = map[type] || { label: type.toUpperCase(), color: '#86868B', bg: '#F5F5F7' };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-extrabold tracking-widest"
      style={{ background: s.bg, color: s.color }}
    >
      {s.label}
    </span>
  );
}

function formatUptime(secs) {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatTime(ts) {
  return new Date(ts).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDuration(secs) {
  if (!secs) return '0s';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

// ── Daily Summary Card ─────────────────────────────────────────

function DailySummaryCard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    axios.get('/api/admin/summary/daily', { params: { date: today } })
      .then(res => setSummary(res.data))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, [today]);

  return (
    <Card accent="#34C759">
      <div className="px-5 py-4 flex items-center justify-between"
        style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        <div className="flex items-center gap-2">
          <BookOpen size={14} style={{ color: '#34C759' }} />
          <h2 className="font-bold text-n-800 text-sm tracking-apple-tight">Resumen del Día</h2>
        </div>
        <span className="text-xs text-n-500 font-mono">{today}</span>
      </div>

      {loading ? (
        <div className="px-5 py-8 flex items-center justify-center">
          <div className="w-6 h-6 border-2 rounded-full animate-spin"
            style={{ borderColor: 'rgba(52,199,89,0.25)', borderTopColor: '#34C759' }} />
        </div>
      ) : !summary || summary.summary?.transmissionCount === 0 ? (
        <div className="px-5 py-8 text-center text-n-500 text-sm">
          Sin actividad hoy
        </div>
      ) : (
        <div className="p-5">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="rounded-md p-3" style={{ background: '#E5F9EC' }}>
              <div className="text-lg font-extrabold tracking-apple-tighter" style={{ color: '#34C759' }}>{summary.summary.transmissionCount}</div>
              <div className="text-xs text-n-600 font-medium">Transmisiones</div>
            </div>
            <div className="rounded-md p-3" style={{ background: '#E6F1FF' }}>
              <div className="text-lg font-extrabold tracking-apple-tighter" style={{ color: '#007AFF' }}>{summary.summary.totalWords || 0}</div>
              <div className="text-xs text-n-600 font-medium">Palabras transcritas</div>
            </div>
            <div className="rounded-md p-3" style={{ background: '#FFF3DE' }}>
              <div className="text-lg font-extrabold tracking-apple-tighter" style={{ color: '#FF9500' }}>{summary.summary.transcribedCount || 0}</div>
              <div className="text-xs text-n-600 font-medium">Con transcripción</div>
            </div>
            <div className="rounded-md p-3" style={{ background: '#FFE5EC' }}>
              <div className="text-sm font-extrabold tracking-apple-tighter" style={{ color: '#FF2D55' }}>{formatDuration(summary.summary.totalDurationSeconds || 0)}</div>
              <div className="text-xs text-n-600 font-medium">Duración total</div>
            </div>
          </div>

          {summary.summary.topUsers?.length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-bold text-n-500 uppercase tracking-widest mb-2">Más activos</div>
              <div className="flex flex-wrap gap-2">
                {summary.summary.topUsers.slice(0, 3).map(u => (
                  <span key={u.username} className="text-xs px-2 py-1 rounded-md text-n-700 font-medium"
                    style={{ background: '#F5F5F7' }}>
                    {u.username} <span className="text-n-500">({u.count})</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {summary.summary.keywords?.length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-bold text-n-500 uppercase tracking-widest mb-2">Palabras clave</div>
              <div className="flex flex-wrap gap-1.5">
                {summary.summary.keywords.slice(0, 6).map(kw => (
                  <span key={kw.word} className="text-xs px-2 py-0.5 rounded-md font-semibold"
                    style={{ background: '#E6F1FF', color: '#007AFF' }}>
                    {kw.word}
                  </span>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={() => navigate('/admin/transcriptions')}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-md text-xs font-semibold text-n-700 transition-colors"
            style={{ background: '#F5F5F7' }}
          >
            <BarChart2 size={12} />
            Ver transcripciones
            <ChevronRight size={12} />
          </button>
        </div>
      )}
    </Card>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [channelStatus, setChannelStatus] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [statsRes, chRes] = await Promise.all([
        axios.get('/api/admin/stats'),
        axios.get('/api/admin/channel-status'),
      ]);
      setStats(statsRes.data);
      setChannelStatus(chRes.data);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 rounded-full animate-spin"
          style={{ borderColor: 'rgba(0,122,255,0.25)', borderTopColor: '#007AFF' }} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-n-800 tracking-apple-tighter">Dashboard</h1>
          <p className="text-sm text-n-500 mt-0.5">Monitoreo en tiempo real</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-n-500">
          <div className="w-2 h-2 rounded-full bg-s-green animate-pulse" />
          Auto-actualiza cada 15s
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Users} label="Total Usuarios"   value={stats?.totalUsers}        color="#007AFF" sub={`${stats?.onlineUsers || 0} online ahora`} />
        <StatCard icon={Wifi}  label="Conectados"       value={stats?.socketConnections} color="#34C759" sub="Conexiones activas" />
        <StatCard icon={Hash}  label="Canales Activos"  value={stats?.activeChannels}    color="#FF9F0A" sub="Con usuarios ahora" />
        <StatCard icon={Mic}   label="TX Hoy"           value={stats?.transmissionsToday} color="#FF375F" sub={`Uptime: ${formatUptime(stats?.uptime || 0)}`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Live activity feed */}
        <Card accent="#007AFF" className="lg:col-span-1">
          <div className="px-5 py-4 flex items-center gap-2"
            style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <Activity size={14} style={{ color: '#007AFF' }} />
            <h2 className="font-bold text-n-800 text-sm tracking-apple-tight">Actividad Reciente</h2>
          </div>
          <div>
            {(!stats?.recentActivity || stats.recentActivity.length === 0) ? (
              <div className="px-5 py-8 text-center text-sm text-n-500">Sin actividad reciente</div>
            ) : stats.recentActivity.map((ev, idx) => (
              <div key={idx} className="px-5 py-3 flex items-center gap-3 hover:bg-black/[0.02] transition-colors"
                style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                <ActivityBadge type={ev.type} />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-semibold text-n-800">{ev.username}</span>
                  {ev.channelName && (
                    <span className="text-xs text-n-500 ml-2">en #{ev.channelName}</span>
                  )}
                  {ev.duration && (
                    <span className="text-xs text-n-400 ml-1">({ev.duration}s)</span>
                  )}
                </div>
                <span className="text-xs text-n-400 flex-shrink-0 font-mono">{formatTime(ev.timestamp)}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Channel status */}
        <Card accent="#AF52DE" className="lg:col-span-1">
          <div className="px-5 py-4 flex items-center gap-2"
            style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <Radio size={14} style={{ color: '#AF52DE' }} />
            <h2 className="font-bold text-n-800 text-sm tracking-apple-tight">Estado de Canales</h2>
          </div>
          <div>
            {channelStatus.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-n-500">Sin canales</div>
            ) : channelStatus.map(ch => (
              <div key={ch.id} className="px-5 py-3 flex items-center justify-between"
                style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${ch.onlineCount > 0 ? 'bg-s-green animate-pulse' : 'bg-n-300'}`} />
                  <div>
                    <div className="text-sm font-semibold text-n-800">{ch.name}</div>
                    {ch.description && <div className="text-xs text-n-500 truncate max-w-[180px]">{ch.description}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-n-500 font-mono">
                    {ch.onlineCount}/{ch.maxUsers}
                  </span>
                  {ch.onlineCount > 0 && (
                    <div className="flex -space-x-1">
                      {ch.users.slice(0, 3).map((u, i) => (
                        <div key={i} className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                          style={{
                            background: `linear-gradient(135deg, ${u.avatarColor || '#007AFF'}, #AF52DE)`,
                            border: '2px solid #fff',
                          }}
                          title={u.username}>
                          {(u.username?.[0] || '?').toUpperCase()}
                        </div>
                      ))}
                      {ch.users.length > 3 && (
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] text-n-600 font-semibold"
                          style={{ background: '#F5F5F7', border: '2px solid #fff' }}>
                          +{ch.users.length - 3}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Daily summary */}
        <div className="lg:col-span-1">
          <DailySummaryCard />
        </div>
      </div>
    </div>
  );
}
